/**
 * Mock избранного: список в localStorage браузера — временно, пока бэкенд не
 * сделал ручки. Гость и вошедший покупатель здесь не различаются: список один
 * на браузер. С API гостевой список доливается на сервер после входа (`merge`).
 */
import { ApiError } from '../../errors'
import { FAVORITES_MAX, type FavoriteDto } from '../contract'

export interface FavoritesBackend {
  list: () => Promise<FavoriteDto[]>
  add: (productId: number) => Promise<void>
  remove: (productId: number) => Promise<void>
  /** Перенести гостевой список на сервер после входа. В mock — нечего переносить. */
  merge: (productIds: number[]) => Promise<void>
}

export const MOCK_FAVORITES_KEY = 'dorixona.mock.favorites.v1'

export interface MockFavoritesOptions {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  now?: () => number
}

export function createMockFavorites({
  storage,
  now = () => Date.now(),
}: MockFavoritesOptions): FavoritesBackend {
  function read(): FavoriteDto[] {
    try {
      const parsed: unknown = JSON.parse(storage.getItem(MOCK_FAVORITES_KEY) ?? '[]')
      return Array.isArray(parsed) ? (parsed as FavoriteDto[]) : []
    } catch {
      return []
    }
  }
  const write = (list: FavoriteDto[]) => {
    storage.setItem(MOCK_FAVORITES_KEY, JSON.stringify(list))
  }
  const reply = <T>(work: () => T) =>
    new Promise<T>((resolve) => {
      resolve(work())
    })

  return {
    list: () => reply(() => [...read()].sort((a, b) => b.added_at.localeCompare(a.added_at))),
    add: (productId) =>
      reply(() => {
        const list = read()
        if (list.some((f) => f.product_id === productId)) return
        if (list.length >= FAVORITES_MAX) {
          throw new ApiError({
            code: 'validation_failed',
            status: 400,
            message: `В избранном не больше ${FAVORITES_MAX} товаров`,
          })
        }
        write([...list, { product_id: productId, added_at: new Date(now()).toISOString() }])
      }),
    remove: (productId) =>
      reply(() => {
        write(read().filter((f) => f.product_id !== productId))
      }),
    merge: () => Promise.resolve(),
  }
}
